import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import { AuthenticatorMode, AuthenticatorType } from "@app/constants/authenticator-type";
import { SvgColor, SvgMatType } from "@app/constants/icons";
import { MaskType } from "@app/constants/masks";
import { Authenticator, AuthenticatorQR, AuthenticatorRequest } from "@app/models/authenticator";
import { ListItem } from "@app/models/listItem";
import { AuthService } from "@app/services/auth.service";
import { FormValidationService } from "@app/services/form-validation.service";
import { HelperService } from "@app/services/helper.service";
import { MaskService } from "@app/services/mask.service";
import { SessionService } from "@app/services/storage/session.service";

@Component({
    selector: 'mwp-update-mfa',
    templateUrl: './update-mfa.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DialogUpdateMFAComponent implements OnInit {
    public maskType = MaskType;
    public svgType = SvgMatType;
    public svgColor = SvgColor;
    public authenticatorType = AuthenticatorType;
    public authenticatorMode = AuthenticatorMode;

    public authenticatorTypes: ListItem[];
    public mode: AuthenticatorMode;
    public selectedAuthenticatorQR: AuthenticatorQR;
    public selectedAuthenticatorType: AuthenticatorType;

    public selectionModeForm: FormGroup;
    public configForm: FormGroup;
    public verifyForm: FormGroup;


    public mfaUpdateStepInstructions: string;
    public configPhoneNumber: boolean;
    public configQR: boolean;
    public verifyMessage: string;
    public verifyMessageKey: string;
    public verifyCodePass: boolean;
    public verifyCodeFailed: boolean;

    public buttonEnabled: boolean;
    private countdown: number = 45;
    private interval: any;

    constructor(
        private dialogRef: MatDialogRef<DialogUpdateMFAComponent>,
        private formBuilder: FormBuilder,
        private sessionService: SessionService,
        private authService: AuthService,
        private helperService: HelperService,
        private maskService: MaskService,
    ) {
    }

    ngOnInit(): void {
        this.mode = AuthenticatorMode.Selection;
        this.setAuthenticatorTypes();

        this.selectionModeForm = this.formBuilder.group({
            verificationType: [null, [Validators.required]]
        });

        this.configForm = this.formBuilder.group({
            phoneNumber: [null, new FormControl([null, [Validators.required, FormValidationService.validPhoneNumber(true)]])],
        });

        this.verifyForm = this.formBuilder.group({
            verifyCode: [null, Validators.required]
        });

        this.setTitle();
    }

    ngOnDestroy(): void {
        this.selectionModeForm = null;
        this.configForm = null;
        this.verifyForm = null;
        this.configPhoneNumber =
            this.configQR = false;
    }

    private setAuthenticatorTypes(): void {
        const l_CellPhone = this.sessionService.session.agent.cellPhone ? `${this.sessionService.session.agent.cellPhone}` : 'Not Configured';
        const l_Phone = this.sessionService.session.agent.phoneNumber ? `${this.sessionService.session.agent.phoneNumber}` : 'Not Configured';
        const l_QR = this.sessionService.session.agent.isQRMFAConfigured ? 'Configured' : 'Not Configured';
        this.authenticatorTypes = [
            { text: `Text Message - ${l_CellPhone}`, value: AuthenticatorType.S },
            { text: `Phone Call - ${l_Phone}`, value: AuthenticatorType.P },
            { text: `Authenticator App - ${l_QR}`, value: AuthenticatorType.Q }
        ];
    }

    private setTitle(): void {
        switch (this.mode) {
            case AuthenticatorMode.Selection:
                this.mfaUpdateStepInstructions = "Please choose the method below you would like to configure.";
                break;

            case AuthenticatorMode.Configuration:
                switch (this.selectedAuthenticatorType) {
                    case AuthenticatorType.S:
                        this.mfaUpdateStepInstructions = "Enter your cell phone number below to receive an authentication code.";
                        break;

                    case AuthenticatorType.P:
                        this.mfaUpdateStepInstructions = "Enter your phone number below to receive a phone call with your authentication code.";
                        break;

                    case AuthenticatorType.Q:
                        this.mfaUpdateStepInstructions = "Scan the QR Code below using your preferred authenticator app.";
                        break;
                }
                break;

            case AuthenticatorMode.Verification:
                this.mfaUpdateStepInstructions = null;
                break;
        }
    }

    public setSelection(): void {
        this.verifyCodePass =
            this.verifyCodeFailed = false;
        const l_VerifyType = this.selectionModeForm.get('verificationType').value;
        this.selectedAuthenticatorType = this.helperService.getEnumByKey(AuthenticatorType, l_VerifyType);
        this.setConfigurationForm(l_VerifyType);
    }

    private setVerifcation(authType: AuthenticatorType): void {
        let l_VerifyMessage: string;
        let l_key: string = 'mfaVerify'
        switch (authType) {
            case AuthenticatorType.S:
                l_VerifyMessage = 'A code has ben sent by Text Message to your phone.';
                l_key += AuthenticatorType.S.toString();
                break;

            case AuthenticatorType.P:
                l_VerifyMessage = 'You will be called shortly with your code.';
                l_key += AuthenticatorType.P.toString();
                break;

            case AuthenticatorType.E:
                l_VerifyMessage = 'A code has ben sent to your email.';
                l_key += AuthenticatorType.E.toString();
                break;

            case AuthenticatorType.Q:
                l_VerifyMessage = 'Open your Authenticator APP for your code.';
                l_key += AuthenticatorType.Q.toString();
                break;
        }
        this.verifyMessage = l_VerifyMessage;
        this.verifyMessageKey = l_key;
        this.mode = AuthenticatorMode.Verification;
        this.setTitle();
    }

    public setConfigurationForm(authType: string): void {
        const l_PhoneNumber = this.configForm.get('phoneNumber');
        l_PhoneNumber.setValue(null);
        l_PhoneNumber.clearValidators();
        l_PhoneNumber.updateValueAndValidity();

        this.configPhoneNumber =
            this.configQR = false;

        switch (authType) {
            case AuthenticatorType.P:
                this.selectedAuthenticatorType = AuthenticatorType.P;
                this.configPhoneNumber = true;
                l_PhoneNumber.setValidators([Validators.required, FormValidationService.validPhoneNumber(true)]);
                l_PhoneNumber.updateValueAndValidity();
                this.mode = AuthenticatorMode.Configuration;
                break;

            case AuthenticatorType.S:
                this.selectedAuthenticatorType = AuthenticatorType.S;
                this.configPhoneNumber = true;
                l_PhoneNumber.setValidators([Validators.required, FormValidationService.validPhoneNumber(true)]);
                l_PhoneNumber.updateValueAndValidity();
                this.mode = AuthenticatorMode.Configuration;
                break;

            case AuthenticatorType.Q:
                this.configQR = true;
                this.authService.mfaGenerateQR().subscribe((response: AuthenticatorQR) => {
                    this.selectedAuthenticatorType = AuthenticatorType.Q;
                    this.selectedAuthenticatorQR = response;
                    this.mode = AuthenticatorMode.Configuration;
                    this.setTitle();
                    this.selectedAuthenticatorType = this.helperService.getEnumByKey(AuthenticatorType, authType);
                });
                break;
        }

        this.setTitle();
        this.selectedAuthenticatorType = this.helperService.getEnumByKey(AuthenticatorType, authType);
    }

    public setPhoneAndSendCode(): void {
        const l_AuthenticatorRequest = new AuthenticatorRequest();
        l_AuthenticatorRequest.phoneNumber = this.maskService.removePhoneMask(this.configForm.get('phoneNumber').value);
        l_AuthenticatorRequest.verificationTypeValue = this.selectedAuthenticatorType;
        this.authService.mfaSetCellphoneSendCode(l_AuthenticatorRequest).subscribe(() => {
            this.startTimer();
            this.setVerifcation(AuthenticatorType[this.selectedAuthenticatorType]);
        });
    }

    public setQRAndSendCode(): void {
        this.authService.mfaSetQR(this.selectedAuthenticatorQR).subscribe(() => {
            this.setVerifcation(AuthenticatorType.Q);
        });
    }

    public resetConfiugration(): void {
        this.mode = AuthenticatorMode.Selection;
        const l_PhoneNumber = this.configForm.get('phoneNumber');
        l_PhoneNumber.setValue(null);
        l_PhoneNumber.clearValidators();
        l_PhoneNumber.updateValueAndValidity();
        this.clearTimer();
    }

    public verifyCode(): void {
        const l_AuthenticatorRequest = new AuthenticatorRequest();
        l_AuthenticatorRequest.verificationTypeValue = this.selectedAuthenticatorType;
        l_AuthenticatorRequest.verifyCode = this.verifyForm.get('verifyCode').value;
        this.authService.mfaVerifyCode(l_AuthenticatorRequest).subscribe((response: Authenticator) => {
            this.verifyCodePass = response.isCodeVerified;
            this.verifyCodeFailed = !response.isCodeVerified;
            if (response.isCodeVerified) {
                switch (this.selectionModeForm.get('verificationType').value) {
                    case this.authenticatorType.P:
                        this.sessionService.session.agent.cellPhone = '(***) ***-' + this.configForm.get('phoneNumber').value.substring(10, 14);
                        break;

                    case this.authenticatorType.P:
                        this.sessionService.session.agent.phoneNumber = '(***) ***-' + this.configForm.get('phoneNumber').value.substring(10, 14);
                        break;

                    case this.authenticatorType.Q:
                        this.sessionService.session.agent.isQRMFAConfigured = true;
                        break;
                }

                this.sessionService.setSession();

                this.selectionModeForm.reset();
                this.configForm.reset();
                this.setAuthenticatorTypes();
                this.mode = AuthenticatorMode.Selection;
                this.verifyForm.reset();

            }
        });
    }

    public resendCode(): void {
        this.startTimer();
        const l_AuthenticatorRequest: AuthenticatorRequest = new AuthenticatorRequest();
        l_AuthenticatorRequest.verificationTypeValue = this.selectedAuthenticatorType;
        this.authService.mfaSendCode(l_AuthenticatorRequest).subscribe((response) => {
        });
    }

    public getResendButtonText(): string {
        if (this.buttonEnabled)
            return 'Resend Code';

        const minutes = Math.floor(this.countdown / 60);
        const seconds = this.countdown % 60;
        return `Resend Code in ${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    }

    private startTimer(): void {
        this.clearTimer();
        this.countdown = 45;
        this.buttonEnabled = false;

        this.interval = setInterval(() => {
            if (this.countdown > 0) {
                this.countdown--;
            } else {
                this.clearTimer();
                this.buttonEnabled = true;
            }
        }, 1000);
    }

    private clearTimer(): void {
        if (this.interval)
            clearInterval(this.interval);
    }

    close(): void {
        this.dialogRef.close();
    }
}